// src/components/HomePage.tsx
import React, { useState, useEffect, useRef } from 'react';
import styles from '../styles/HomePage.module.css';

import campusImage1 from '../resources/R.jpg';
import campusImage2 from '../resources/img1.jpg';
import campusImage3 from '../resources/img3.jpg';
import VCImage from '../resources/OIP.jpg';

import newsImg1 from '../resources/image1.png';
import newsImg2 from '../resources/image2.png';
import newsImg3 from '../resources/image3.png';

import whyIcon from '../resources/seo-report.png';
import globeIcon from '../resources/megaphone.png';
import gradIcon from '../resources/graduation-cap.png';
import bookIcon from '../resources/team.png';
import arrowIcon from '../resources/right-arrow.png';

import thumb1 from '../resources/book.jpg';
import thumb2 from '../resources/book.jpg';
import thumb3 from '../resources/book.jpg';
import thumb4 from '../resources/book.jpg';

interface Highlight {
  thumb: string;
  title: string;
  author: string;
  desc: string;
  link: string;
}

const highlights: Highlight[] = [
  { thumb: thumb1, title: 'Epistemology of Testimony in Digital Age', author: 'Dr. Adaora Obi', desc: 'This project investigates how trust and knowledge acquisition are shaped by digital platforms and social media.', link: '/research/epistemology' },
  { thumb: thumb2, title: 'African Philosophy and Decolonial Thought', author: 'Prof. Jide Okoro', desc: 'Exploring indigenous African philosophical systems and their contributions to global decolonial discourse.', link: '/research/african-philosophy' },
  { thumb: thumb3, title: 'Consciousness and Quantum Mechanics', author: 'Dr. Biodun Adewale', desc: 'A theoretical inquiry into the intersection of quantum physics and the philosophy of mind, examining non-reductive views.', link: '/research/quantum-consciousness' },
  { thumb: thumb4, title: 'The Ethics of Climate Change Migration', author: 'Ms. Chiamaka Eze (PhD Candidate)', desc: 'Examining the moral obligations of states and international bodies towards populations displaced by climate disruptions.', link: '/research/climate-migration' },
];

interface Feature {
  icon: string;
  title: string;
  description: string;
}

const features: Feature[] = [
  { icon: whyIcon, title: 'Why study at Unical?', description: '"Shape Your Future with Us" Study at a university renowned for academic excellence, cultural diversity, and an innovative learning environment.' },
  { icon: globeIcon, title: 'Global Collaborations', description: '"Collaborate with world-renowned institutions and enjoy access to international learning and research opportunities."' },
  { icon: gradIcon, title: 'Graduate Admissions', description: '"Unlock your potential with advanced degrees tailored to meet global standards and your career aspirations."' },
  { icon: bookIcon, title: 'Scholarships & fees', description: '"Benefit from affordable tuition and a wide range of scholarships designed to support your academic journey."' },
];

interface Leader {
  id: number;
  name: string;
  title: string;
  bio: string;
  department: string;
  imageUrl: string;
}

const leaders: Leader[] = [
  { id: 1, name: 'Prof. Florence Obi', title: 'Vice Chancellor', bio: 'Distinguished scholar with 25+ years in academic leadership. PhD in Educational Psychology.', department: 'University Administration', imageUrl: VCImage },
  { id: 2, name: 'Dr. Emmanuel Eyo', title: 'Dean of Humanities', bio: 'Expert in African philosophy with numerous publications on indigenous knowledge systems.', department: 'Faculty of Humanities', imageUrl: VCImage },
  { id: 3, name: 'Prof. Anya Nkachi', title: 'Head of Department', bio: "Leading scholar in contemporary metaphysics and author of 'The Fabric of Reality'.", department: 'Philosophy Department', imageUrl: VCImage },
  { id: 4, name: 'Dr. Chiedozie Okoro', title: 'Deputy HOD', bio: 'Specializes in applied ethics and moral philosophy with focus on post-colonial societies.', department: 'Philosophy Department', imageUrl: VCImage },
  { id: 5, name: 'Prof. Anya Nkachi', title: 'Head of Department', bio: "Leading scholar in contemporary metaphysics and author of 'The Fabric of Reality'.", department: 'Philosophy Department', imageUrl: VCImage },
  { id: 6, name: 'Dr. Chiedozie Okoro', title: 'Deputy HOD', bio: 'Specializes in applied ethics and moral philosophy with focus on post-colonial societies.', department: 'Philosophy Department', imageUrl: VCImage },
];

type HeroSlide = { image: string; title: string; buttonText: string };

// Pie chart component
const PieChart: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Department statistics data
  const stats = {
    undergraduates: 1420,
    phd: 286,
    professors: 74
  };
  
  const total = stats.undergraduates + stats.phd + stats.professors;
  
  // Calculate percentages
  const percentages = {
    undergraduates: Math.round((stats.undergraduates / total) * 100),
    phd: Math.round((stats.phd / total) * 100),
    professors: Math.round((stats.professors / total) * 100)
  };
  
  // Color scheme
  const colors = {
    undergraduates: '#4e79a7',
    phd: '#f28e2c',
    professors: '#e15759'
  };

  // Draw the pie chart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw pie slices
    let startAngle = 0;
    
    // Undergraduates slice
    const undergradAngle = (percentages.undergraduates / 100) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, startAngle, startAngle + undergradAngle);
    ctx.closePath();
    ctx.fillStyle = colors.undergraduates;
    ctx.fill();
    startAngle += undergradAngle;
    
    // PhD slice
    const phdAngle = (percentages.phd / 100) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, startAngle, startAngle + phdAngle);
    ctx.closePath();
    ctx.fillStyle = colors.phd;
    ctx.fill();
    startAngle += phdAngle;
    
    // Professors slice
    const profAngle = (percentages.professors / 100) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, startAngle, startAngle + profAngle);
    ctx.closePath();
    ctx.fillStyle = colors.professors;
    ctx.fill();
    
    // Draw center hole for donut effect
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius * 0.5, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = 'white';
    ctx.fill();
    
    // Draw center text
    ctx.fillStyle = '#2c3e50';
    ctx.font = 'bold 16px "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Total', centerX, centerY - 10);
    ctx.font = 'bold 24px "Segoe UI", sans-serif';
    ctx.fillText(total.toString(), centerX, centerY + 15);
  }, [percentages, colors, total]);

  return (
    <div className={styles['pie-chart-container']}>
      <div className={styles['chart-header']}>
        <h2 className={styles['chart-title']}>Department Composition</h2>
        <p className={styles['chart-subtitle']}>Academic Year 2025/2026</p>
      </div>
      
      <div className={styles['chart-content']}>
        <div className={styles['chart-canvas']}>
          <canvas ref={canvasRef} width={300} height={300} />
        </div>
        
        <div className={styles['chart-legend']}>
          <div className={styles['legend-item']}>
            <div className={styles['legend-color']} style={{ backgroundColor: colors.undergraduates }}></div>
            <div className={styles['legend-info']}>
              <span className={styles['legend-label']}>Undergraduates</span>
              <div className={styles['legend-values']}>
                <span className={styles['legend-value']}>{stats.undergraduates}</span>
                <span className={styles['legend-percentage']}>{percentages.undergraduates}%</span>
              </div>
            </div>
          </div>
          
          <div className={styles['legend-item']}>
            <div className={styles['legend-color']} style={{ backgroundColor: colors.phd }}></div>
            <div className={styles['legend-info']}>
              <span className={styles['legend-label']}>PhD Students</span>
              <div className={styles['legend-values']}>
                <span className={styles['legend-value']}>{stats.phd}</span>
                <span className={styles['legend-percentage']}>{percentages.phd}%</span>
              </div>
            </div>
          </div>
          
          <div className={styles['legend-item']}>
            <div className={styles['legend-color']} style={{ backgroundColor: colors.professors }}></div>
            <div className={styles['legend-info']}>
              <span className={styles['legend-label']}>Professors</span>
              <div className={styles['legend-values']}>
                <span className={styles['legend-value']}>{stats.professors}</span>
                <span className={styles['legend-percentage']}>{percentages.professors}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className={styles['chart-insight']}>
        <div className={styles['insight-card']}>
          <h3>Student-to-Professor Ratio</h3>
          <p>{Math.round((stats.undergraduates + stats.phd) / stats.professors)}:1</p>
        </div>
        <div className={styles['insight-card']}>
          <h3>Graduate Percentage</h3>
          <p>{Math.round((stats.phd / (stats.undergraduates + stats.phd)) * 100)}%</p>
        </div>
      </div>
    </div>
  );
};

const HomePage: React.FC = () => {
  const heroSlides: HeroSlide[] = [
    { image: campusImage1, title: 'Cultivating Critical Minds for a Complex World.', buttonText: 'Explore Our Programs' },
    { image: campusImage2, title: 'Where Philosophy Meets Modern Challenges.', buttonText: 'View Faculty Profiles' },
    { image: campusImage3, title: "Shaping Tomorrow's Thought Leaders Today.", buttonText: 'Discover Research' },
  ];

  const [currentSlide, setCurrentSlide] = useState(0);
  useEffect(() => {
    const iv = setInterval(() => setCurrentSlide(i => (i + 1) % heroSlides.length), 4000);
    return () => clearInterval(iv);
  }, [heroSlides.length]);

  return (
    <div className={styles['home-page']}>
      {/* Hero Section */}
      <header className={styles.hero}>
        {heroSlides.map((slide, i) => (
          <div
            key={i}
            className={`${styles.slide} ${i === currentSlide ? styles.active : ''}`}
            style={{ '--bg': `url(${slide.image})` } as React.CSSProperties}
          >
            <div className={styles.overlay} />
            <div className={styles.content}>
              <h3 className={styles['hero__title']}>{slide.title}</h3>
              <button className={styles['hero__btn']}>{slide.buttonText}</button>
            </div>
          </div>
        ))}
      </header>

      {/* Main Content */}
      <main className={styles.container}>
        <section className={styles['features-section']}>
          <div className={styles['features-container']}>
            {features.map((f, i) => (
              <div key={i} className={styles['feature-card']}>
                <img src={f.icon} alt="" className={styles['feature-icon']} />
                <h3 className={styles['feature-title']}>{f.title}</h3>
                <p className={styles['feature-desc']}>{f.description}</p>
                <button className={styles['feature-btn']}>
                  <img src={arrowIcon} alt="→" />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className={styles['research-highlights']}>
          <div className={styles['rh-container']}>
            <h2 className={styles.head}>Research Highlights</h2>
            <div className={styles['rh-grid']}>
              {highlights.map((h, i) => (
                <div key={i} className={styles['rh-card']}>
                  <div className={styles['rh-thumb']}>
                    <img src={h.thumb} alt="" />
                  </div>
                  <h3 className={styles['rh-title']}>{h.title}</h3>
                  <p className={styles['rh-author']}>{h.author}</p>
                  <p className={styles['rh-desc']}>{h.desc}</p>
                  <a href={h.link} className={styles['rh-learn']}>Learn More</a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Department Statistics Pie Chart - Placed lower on the page */}
        <section className={styles['department-stats']}>
          <PieChart />
        </section>

        <section className={styles['academic-leaders']}>
          <div className={styles['leadership-container']}>
            <div className={styles['leadership-header']}>
              <h2 className={styles['leadership-title']}>Academic Leadership</h2>
              <div className={styles['title-divider']} />
              <p className={styles['leadership-subtitle']}>Meet our distinguished academic leaders guiding the Philosophy Department</p>
            </div>
            <div className={styles['leaders-grid']}>
              {leaders.map(l => (
                <div key={l.id} className={styles['leader-profile']}>
                  <div className={styles['leader-img-container']}>
                    <img src={l.imageUrl} alt={l.name} className={styles['leader-photo']} />
                    <span className={styles['leader-department']}>{l.department}</span>
                  </div>
                  <div className={styles['leader-details']}>
                    <h3 className={styles['leader-name']}>{l.name}</h3>
                    <p className={styles['leader-position']}>{l.title}</p>
                    <p className={styles['leader-bio']}>{l.bio}</p>
                    <button className={styles['contact-button']}>
                      Contact
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                        <polyline points="22,6 12,13 2,6" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className={styles['events-news-section']}>
          <div className={styles.container1}>
            <div className={styles['section-header']}>
              <h2 className={styles['section-title']}>Upcoming Events & News</h2>
              <div className={styles.divider}></div>
              <p className={styles['section-subtitle']}>Stay updated with the latest happenings in our department</p>
            </div>
            <div className={styles['content-grid']}>
              {/* Events Column */}
              <div className={styles['events-column']}>
                <h3 className={styles['column-title']}>Upcoming Events</h3>
               <div className={styles['event-card']}>
                  <div className={styles['event-date']}>
                    <span className={styles['date-day']}>24</span>
                    <span className={styles['date-month']}>OCT</span>
                  </div>
                  <div className={styles['event-details']}>
                    <h4 className={styles['event-title']}>
                      Metaphysical Creators Conference
                    </h4>
                    <div className={styles['event-meta']}>
                      <span className={styles['event-time']}>
                        <i className="far fa-clock"></i> 10:00 AM - 4:00 PM
                      </span>
                      <span className={styles['event-location']}>
                        <i className="fas fa-map-marker-alt"></i> President's College Hall
                      </span>
                    </div>
                    <p className={styles['event-desc']}>
                      Join leading philosophers as they explore new frontiers in metaphysical thought and creativity.
                    </p>
                    <button className={styles['event-btn']}>Register Now</button>
                  </div>
                </div>

                <div className={styles['event-card']}>
                  <div className={styles['event-date']}>
                    <span className={styles['date-day']}>01</span>
                    <span className={styles['date-month']}>NOV</span>
                  </div>
                  <div className={styles['event-details']}>
                    <h4 className={styles['event-title']}>
                      Philosophy and Public Policy Lecture
                    </h4>
                    <div className={styles['event-meta']}>
                      <span className={styles['event-time']}>
                        <i className="far fa-clock"></i> 2:00 PM - 4:00 PM
                      </span>
                      <span className={styles['event-location']}>
                        <i className="fas fa-map-marker-alt"></i> Main Auditorium
                      </span>
                    </div>
                    <p className={styles['event-desc']}>
                      Distinguished speakers discuss how philosophical principles shape effective public policy.
                    </p>
                    <button className={styles['event-btn']}>Register Now</button>
                  </div>
                </div>

                <div className={styles['event-card']}>
                  <div className={styles['event-date']}>
                    <span className={styles['date-day']}>19</span>
                    <span className={styles['date-month']}>JAN</span>
                  </div>
                  <div className={styles['event-details']}>
                    <h4 className={styles['event-title']}>Research Grant Workshop</h4>
                    <div className={styles['event-meta']}>
                      <span className={styles['event-time']}>
                        <i className="far fa-clock"></i> 9:00 AM - 12:00 PM
                      </span>
                      <span className={styles['event-location']}>
                        <i className="fas fa-map-marker-alt"></i> Research Building
                      </span>
                    </div>
                    <p className={styles['event-desc']}>
                      Learn how to secure funding for your philosophical research projects.
                    </p>
                    <button className={styles['event-btn']}>Register Now</button>
                  </div>
                </div>

                <div className={styles['view-all']}>
                  <button className={styles['view-all-btn']}>
                    View All Events <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>

              {/* News Column */}
              <div className={styles['news-column']}>
                <h3 className={styles['column-title']}>Department News</h3>

                <div className={styles['news-card']}>
                   <img src={newsImg1} alt="Ethics in AI Conference" className={styles['news-image']} />
                  <div className={styles['news-content']}>
                    <span className={styles['news-date']}>October 15, 2025</span>
                    <h4 className={styles['news-title']}>
                      Department Hosts Ethics in AI Conference
                    </h4>
                    <p className={styles['news-excerpt']}>
                      The Philosophy Department successfully hosted its annual conference focusing on the ethical implications of artificial intelligence.
                    </p>
                    <a href="#" className={styles['news-link']}>
                      Read Full Story
                    </a>
                  </div>
                </div>

                <div className={styles['news-card']}>
                  <img src={newsImg2} alt="Ethics in AI Conference" className={styles['news-image']} />
                  <div className={styles['news-content']}>
                    <span className={styles['news-date']}>October 8, 2025</span>
                    <h4 className={styles['news-title']}>
                      New Philosophy Journal Launched
                    </h4>
                    <p className={styles['news-excerpt']}>
                      Our department has launched a new peer-reviewed journal focusing on African philosophical traditions.
                    </p>
                    <a href="#" className={styles['news-link']}>
                      Read Full Story
                    </a>
                  </div>
                </div>

                <div className={styles['news-card']}>
                 <img src={newsImg3} alt="Ethics in AI Conference" className={styles['news-image']} />
                  <div className={styles['news-content']}>
                    <span className={styles['news-date']}>September 28, 2025</span>
                    <h4 className={styles['news-title']}>
                      Professor Wins Prestigious Award
                    </h4>
                    <p className={styles['news-excerpt']}>
                      Dr. Anya Nkachi has received the International Philosophy Award for her groundbreaking work in metaphysics.
                    </p>
                    <a href="#" className={styles['news-link']}>
                      Read Full Story
                    </a>
                  </div>
                </div>

                <div className={styles['view-all']}>
                  <button className={styles['view-all-btn']}>
                    View All News <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default HomePage;